import { Grid, Stack } from "@mui/material";
import { GridSortModel } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { col4 } from "../../../../common/utils";
import NumericValueWidget from "../../../../components/charts/NumericValueWidget";
import DropdownSelect from "../../../../components/common/DropdownSelect";
import { TransportPerspectiveSelector } from "../../../../components/common/TransportPerspectiveSelector";
import { View } from "../../../../components/layout/View";
import { PageType } from "../../../../config/page.types";
import { useFiltersRegistry } from "../../../../context/FiltersProvider";
import { usePageConfigurationState } from "../../../../context/PageConfigrations/usePageConfigurationState";
import { EntitiesListDTO, ResultsBasePayload, TripsPageDTO } from "../../../../datasources/api-client/data-contracts";
import { ApiRoutes } from "../../../../datasources/networking/apiRoutes";
import { useNavigationPreferences } from "../../../../hooks/usePreferences";
import { useReadResource } from "../../../../hooks/useResource";
import TableTransportGreenrouter from "./components/TableTransportGreenrouter";
import { TableEntitiesGreenRouter } from "./TableEntitiesGreenRouter";

// Map frontend sort fields to API sort fields
type SortableApiField = "date" | "description" | "distance" | "weight" | "emissions";
type SortableTransportsField = SortableApiField | "id" | "origin" | "destination" | "temperature" | "dedicated";
type SortableEntitiesField = SortableApiField | "transportDescription" | "originDescription" | "destinationDescription";

const SORT_OVERRIDE: Partial<Record<string, SortableApiField>> = {
	tCo2eWtw: "emissions",
	tCo2eTtw: "emissions",
	energyWtw: "emissions",
	energyTtw: "emissions",
};

type EmissionsKey = "co2Ttw" | "co2Wtw";

const TransportGreenrouter = () => {
	const { t } = useTranslation(["result.transport", "common"]);

	const { appliedFilters } = useFiltersRegistry();
	const { navigationPreferences } = useNavigationPreferences();

	const isOperational = navigationPreferences.transportsPerspective === "operational";

	// Page configuration stored in localStorage
	const [transportConfig, setTransportConfig] = usePageConfigurationState(PageType.ResultsTransportGreenRouter, {
		emissionsKey: "co2Ttw",
		page: 0,
		pageSize: 10,
		sortField: undefined,
		sortDirection: undefined,
		columnVisibilityModel: {},

		entityPage: 0,
		entityEmissionsKey: "co2Ttw",
		entityColumnVisibilityModel: {},
	});

	const [entityConfig, setEntityConfig] = usePageConfigurationState(PageType.ResultsTransportGreenRouterEntities, {
		emissionsKey: "co2Ttw",
		page: 0,
		pageSize: 10,
		sortField: undefined,
		sortDirection: undefined,
		columnVisibilityModel: {},

		entityPage: 0,
		entityEmissionsKey: "co2Ttw",
		entityColumnVisibilityModel: {},
	});

	const config = isOperational ? transportConfig : entityConfig;

	// Convert stored sort config to DataGrid format
	const sortModel: GridSortModel = config.sortField ? [{ field: config.sortField, sort: config.sortDirection }] : [];

	// Convert stored pagination config to DataGrid format
	const paginationModel = {
		page: config.page,
		pageSize: config.pageSize,
	};

	// Resolve emission KPI field based on selected emission type
	const emissionType = config.emissionsKey === "co2Ttw" ? "totalEmissionsTtw" : "totalEmissionsWtw";

	// Dropdown options for emission selection
	const dropdownSelectEmissions = [
		{ key: "co2Ttw", label: t("common:ttw select") },
		{ key: "co2Wtw", label: t("common:wtw select") },
	];

	// Change selected emission type
	const handleEmissionChange = (key: string) => {
		if (isOperational) {
			setTransportConfig({ emissionsKey: key as EmissionsKey });
			return;
		}

		setEntityConfig({ emissionsKey: key as EmissionsKey });
	};

	const sortBy = sortModel[0]?.field
		? isOperational
			? (SORT_OVERRIDE[sortModel[0].field] ?? (sortModel[0].field as SortableTransportsField))
			: (SORT_OVERRIDE[sortModel[0].field] ?? (sortModel[0].field as SortableEntitiesField))
		: undefined;

	const sortDir = sortModel[0]?.sort;

	const { data, isLoading, reload } = useReadResource<ResultsBasePayload, TripsPageDTO | EntitiesListDTO>(
		{
			route: isOperational ? ApiRoutes.ResultsTransportGreenrouter : ApiRoutes.ResultsEntitiesGreenrouter,
			query: {
				page: paginationModel.page,
				pageSize: paginationModel.pageSize,
				...(sortBy && { sortBy }),
				...(sortDir && { sortDir }),
			},
		},
		{
			readMethod: "post",
		},
		{ ...appliedFilters.payload }
	);

	return (
		<View pageType={PageType.ResultsTransportGreenRouter} sx={{ maxWidth: "100vw", overflowX: "hidden" }}>
			<Grid container spacing={2}>
				<Grid size={12}>
					<Stack direction="row" spacing={2} justifyContent="flex-end">
						<DropdownSelect
							text={t("display for")}
							value={config.emissionsKey}
							loading={isLoading}
							onChange={handleEmissionChange}
							dropdownItems={dropdownSelectEmissions}
							variant="standard"
						/>
						<TransportPerspectiveSelector />
					</Stack>
				</Grid>

				<Grid size={col4} height="150px">
					<NumericValueWidget
						title={t("emissions")}
						value={data?.[emissionType]?.value}
						measure={data?.[emissionType]?.measure}
						isLoading={isLoading}
						onReload={reload}
					/>
				</Grid>

				<Grid size={col4} height="150px">
					<NumericValueWidget
						title={t("hub emissions and nodes")}
						value={data?.hubEmissions?.value}
						measure={data?.hubEmissions?.measure}
						isLoading={isLoading}
						onReload={reload}
					/>
				</Grid>

				<Grid size={col4}>
					<NumericValueWidget
						title={t("number of transports")}
						value={data?.itemsCount}
						isLoading={isLoading}
						onReload={reload}
					/>
				</Grid>

				<Grid size={12} minHeight={500} sx={{ minWidth: 0 }}>
					{navigationPreferences.transportsPerspective === "operational" && (
						<TableTransportGreenrouter data={data} isLoading={isLoading} reload={reload} />
					)}
					{navigationPreferences.transportsPerspective === "logical" && (
						<TableEntitiesGreenRouter data={data} isLoading={isLoading} reload={reload} />
					)}
				</Grid>
			</Grid>
		</View>
	);
};

export default TransportGreenrouter;
